import React, { useEffect, useMemo } from 'react';
import { HolidayReportDatePicker } from "./HolidayReportDatePicker";
import isMobile from "is-mobile";
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import styled from "styled-components";
import { HeadlineSmall } from '@shared/ui/atoms/fonts/Headline';
import { useTranslation } from "react-i18next";
import Colours from '@shared/ui/atoms/Colours';
import {
  DownloadHighLevelHolidayReportButton
} from "@features/reporting/pages/components/DownloadHighLevelHolidayReportButton";
import { DownloadDetailedHolidayReportButton } from "@features/reporting/pages/components/DownloadDetailedHolidayReportButton";
import { useDispatch, useSelector } from "react-redux";
import { loadUserHolidayInfo, selectHolidayReportingDate } from "../../models/holiday-reports.duck";
import { DownloadDetailedAbsenceReportButton } from "@features/reporting/pages/components/DownloadDetailedAbsenceReportButton";
import { selectConfig } from '@shared/store/config.duck';
import { selectCurrentUser } from '@shared/store/auth.duck';
import { isCompanyAdminUser, isOfficeAdminUser } from '@shared/utils/AccessControl';

export function HolidayReportSidebarContent(props: Props) {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const selectedDate = useSelector(selectHolidayReportingDate);
  const config = useSelector(selectConfig);
  const user = useSelector(selectCurrentUser);
  const canAccessReport = isOfficeAdminUser(user);
  const canEditHolidayAllowances = isCompanyAdminUser(user);
  const holidayTrackingEnabled = user?.companyEntity.enableHolidayBooking;

  useEffect(() => {
    dispatch(loadUserHolidayInfo())
  }, [selectedDate, dispatch]);

  const holidayYearStartDate = useMemo(() => {
    let date = selectedDate.clone();
    date.month(config.holidayYearRecurringMonth-1);
    date.date(config.holidayYearRecurringDay);
    return date;
  }, [config, selectedDate]);

  const holidayYearEndDate = useMemo(() => {
    let date = holidayYearStartDate.clone();
    date.add(1, 'years');
    date.subtract(1, 'days');
    return date;
  }, [holidayYearStartDate]);

  return (
    <>
      {!isMobile() && <HolidayReportDatePicker style={{marginBottom: 24}} />}

      <OutlineButton text={'holidays.add-new-leave'} link={'/holidays/add'} fullWidth={true} style={{marginBottom: 24}}  />

      <ReportContentCard>
        <HeadlineSmall style={{marginBottom: 24, marginRight: 32}} color={Colours.darkBlue}>{t('reports-page.download-holiday-summary')}</HeadlineSmall>

        <DownloadHighLevelHolidayReportButton selectedDate={selectedDate} disabled={canAccessReport} />
        <DownloadDetailedHolidayReportButton selectedDate={selectedDate} disabled={canAccessReport} />
      </ReportContentCard>

      <ReportContentCard style={{marginBottom: 16}}>
        <HeadlineSmall style={{marginBottom: 24, marginRight: 32}} color={Colours.darkBlue}>{t('reports-page.download-absence-summary')}</HeadlineSmall>
        <OutlineButton fullWidth={true} style={{marginBottom: 12}} disabled={true}
                       text={'reports-page.holidays-report.high-level-report'} />
        <DownloadDetailedAbsenceReportButton fromDate={holidayYearStartDate} toDate={holidayYearEndDate} disabled={canAccessReport} />
      </ReportContentCard>

      {holidayTrackingEnabled && canEditHolidayAllowances && !isMobile() && <OutlineButton link={'/holidays/settings/allowance'}
                                                                                           style={{'margin': '0 auto'}}
                                                                                           text={'reports-page.edit-holiday-allowance'} />}
    </>
  )
}

interface Props {
}

const ReportContentCard = styled.div`
  border-radius: 24px;
  background: #E6FCFF;
  display: flex;
  padding: 32px 24px;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  margin-bottom: 16px;
  
  button {
    margin-bottom: 12px;
    min-width: 100%;
    width: 100%;
  }
`
